import { Box, CircularProgress, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';
import { DateInput, useFormContext } from 'framework/form';
import { Button } from 'framework/ui/mui-builder';
import { DATE_FORMAT, useDateService } from 'framework/utils';
import { PROJECT_REVIEW_COMPARE_MODE } from './constants';

const ProjectReviewForm = ({
  previousReviewDate,
  hasReview,
  projectStartDate,
  projectEndDate,
}: {
  previousReviewDate?: string;
  hasReview?: boolean;
  projectStartDate?: string;
  projectEndDate?: string;
}) => {
  const { data = {}, isSubmitEnabled, onChangeCallback, submitForm } = useFormContext();
  const { getToday } = useDateService();
  const isSincePreviousReview = data.compareMode === PROJECT_REVIEW_COMPARE_MODE.SINCE_PREVIOUS_REVIEW;
  const isReviewStarting = !isSubmitEnabled;
  const canSubmit = isSincePreviousReview
    ? !!data.previousReviewDate && !!data.reviewDate
    : !!data.startDate && !!data.endDate;

  const today = getToday().format(DATE_FORMAT.API_ONLY_DATE);
  const capAtToday = (date?: Nullable<string>) => (date && date < today ? date : today);

  const handleCompareModeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const compareMode = event.target.value;

    onChangeCallback?.({
      compareMode,
      previousReviewDate: compareMode === PROJECT_REVIEW_COMPARE_MODE.SINCE_PREVIOUS_REVIEW ? previousReviewDate : null,
    });
  };

  return (
    <Box sx={{ pb: 4 }}>
      <Typography variant="subtitle1" mb={1}>
        Select compare mode
      </Typography>

      <RadioGroup row value={data.compareMode} onChange={handleCompareModeChange} sx={{ gap: { xs: 1, sm: 3 }, mb: 2 }}>
        <FormControlLabel
          value={PROJECT_REVIEW_COMPARE_MODE.SINCE_PREVIOUS_REVIEW}
          control={<Radio size="small" />}
          label="Since previous review"
          disabled={!previousReviewDate || isReviewStarting}
        />
        <FormControlLabel
          value={PROJECT_REVIEW_COMPARE_MODE.CUSTOM_PERIOD}
          control={<Radio size="small" />}
          label="Custom period"
          disabled={isReviewStarting}
        />
      </RadioGroup>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(220px, 280px))' },
          gap: 2,
          mb: 5,
        }}
      >
        {isSincePreviousReview ? (
          <>
            <DateInput source="previousReviewDate" label="Previous review" disabled />
            <DateInput
              source="reviewDate"
              label="Review date"
              minDate={data.previousReviewDate}
              maxDate={today}
              disabled={isReviewStarting}
            />
          </>
        ) : (
          <>
            <DateInput
              source="startDate"
              label="Start date"
              minDate={projectStartDate}
              maxDate={capAtToday(data.endDate || projectEndDate)}
              disabled={isReviewStarting}
            />
            <DateInput
              source="endDate"
              label="End date"
              minDate={data.startDate || projectStartDate}
              maxDate={capAtToday(projectEndDate)}
              disabled={isReviewStarting}
            />
          </>
        )}
      </Box>

      <Box mb={0} display="flex" justifyContent="flex-end">
        <Button
          variant="contained"
          startIcon={isReviewStarting ? <CircularProgress color="inherit" size={16} /> : undefined}
          onClick={() => submitForm?.()}
          disabled={!canSubmit || isReviewStarting}
        >
          {hasReview ? 'Restart review' : 'Start review'}
        </Button>
      </Box>
    </Box>
  );
};

export default ProjectReviewForm;
